import { REACT_APP_FRONTEND_HOSTNAME } from 'env'
import { v4 } from 'uuid'
type sentMessages = Map<
  string,
  { sent: number; received?: number; delay?: number; length: number; shouldDiscard?: boolean }
>

const STATS_RESOLUTION_MS = 1000 * 30 // sent packets are collected for this many milliseconds before generating a new entry
const PAYLOAD_LENGTH_TRESHOLD_BEFORE_DISPATCH = 1024 * 48 // how long the packet should be before it is auto-dispatched
const TELEMETRY_BEACON_ENDPOINT =
  REACT_APP_FRONTEND_HOSTNAME === 'localhost'
    ? 'http://localhost:3333/dev/websocket'
    : 'https://utjvl7cfw2.execute-api.us-west-2.amazonaws.com/dev/websocket'

export enum EVENT_TYPES {
  'msg:sent' = 'msg:sent',
  'msg:received' = 'msg:received',
}

export type statisticMeasure = {
  min?: number
  max?: number
  average?: number
  median?: number
  stdev?: number
}

type logPoint = {
  time: number
  count: number
  size: statisticMeasure
  delay: statisticMeasure
}

type data = {
  start: number
  sessionId: string
  eventMap: Partial<{
    [key in EVENT_TYPES]: logPoint[]
  }>
}

/**
 * Given an array of numbers, find the biggest number inside it
 * Unlike Math.max(...arr), this will not return Infinity on an empty array
 * @param arr an array of numbers
 * @returns the biggest number or `undefined` if `arr` is empty
 */
function computeMax(arr: number[]): number | undefined {
  if (arr.length === 0) return
  return Math.max(...arr)
}

/**
 * Given an array of numbers, find the smallest number inside it
 * Unlike Math.min(...arr), this will not return -Infinity on an empty array
 * @param arr an array of numbers
 * @returns the smallest number or `undefined` if `arr` is empty
 */
function computeMin(arr: number[]): number | undefined {
  if (arr.length === 0) return
  return Math.min(...arr)
}

/**
 * Sum all the numbers inside an array
 * Some people are afraid of reducers so this function hides it away
 * @param arr an array of numbers
 * @returns the sum or `undefined` if `arr` is empty
 */
function computeSum(arr: number[]): number | undefined {
  if (arr.length === 0) return
  return arr.reduce((acc, item) => (acc += item))
}

/**
 * Get the average of all numbers inside the array, clamped to 2 decimal points of precision
 * @param arr an array of numbers
 * @returns the average/mean or `undefined` if `arr` is empty
 */
function computeMean(arr: number[]): number | undefined {
  if (arr.length === 0) return
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return parseFloat((computeSum(arr)! / arr.length).toFixed(2))
}

/**
 * Get the median in an array of numbers
 * @param arr an array of numbers
 * @returns the median or `undefined` if `arr` is empty
 */
function computeMedian(arr: number[]): number | undefined {
  if (arr.length === 0) return
  arr.sort((a, b) => a - b)
  const mid = arr.length / 2
  return mid % 1 ? arr[mid - 0.5] : (arr[mid - 1] + arr[mid]) / 2
}

/**
 * Get the stdev in an array of numbers
 * @param arr an array of numbers
 * @returns the standard deviation or `undefined` if `arr` is empty
 */
function computeStdev(arr: number[]): number | undefined {
  if (arr.length === 0) return
  const n = arr.length
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const mean = computeMean(arr)!
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return parseFloat(Math.sqrt(computeSum(arr.map((x) => (x - mean) ** 2))! / n).toFixed(2))
}

class _WebsocketLogger {
  static accumulatedData: data
  static _sentMessages: sentMessages = new Map()
  static sessionId: string
  constructor() {
    if (!_WebsocketLogger.sessionId) {
      _WebsocketLogger.sessionId = v4()
    }
    if (!_WebsocketLogger.accumulatedData) {
      _WebsocketLogger.accumulatedData = {
        start: new Date().getTime(),
        sessionId: _WebsocketLogger.sessionId,
        eventMap: {},
      }
    }
  }
  /**
   * logs a websocket message sent by the client
   */
  logSentMessage = (payload: any) => {
    const length = JSON.stringify(payload).length
    _WebsocketLogger._sentMessages.set(`${payload.client_uuid}-v${payload.version}`, {
      sent: new Date().getTime(),
      length: length,
    })
  }

  /**
   * logs a websocket message received from the server
   *
   * Because all sent messages are echoed back to the client, this function will also
   * look into the received message and attempt to derive the delay for previously sent messages
   */
  logReceivedMessage = (payload: any) => {
    payload.versions.forEach((version: number) => {
      const key = `${payload.meta.client_uuid}-v${version}`
      if (_WebsocketLogger._sentMessages.has(key)) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const msg = _WebsocketLogger._sentMessages.get(key)!
        const received = new Date().getTime()
        _WebsocketLogger._sentMessages.set(key, { ...msg, received: received, delay: received - msg.sent })
      }
    })
  }

  /**
   * This function does three things:
   * - Gets all the accumulated messages so far and puts them inside `accumulatedData`, to be prepared
   * for dispatching.
   * - Clears the current message queue. This is done to preserve memory, prevent counting
   * messages more than once, and to avoid having to keep building the packet every time a message
   * is added (which would be needed since we have to check the payload size)
   * - Triggers `maybeDispatchPayload`
   */
  static flush() {
    const msgs = Array.from(_WebsocketLogger._sentMessages.values())
    if (msgs.length > 0) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const delays = msgs.filter((m) => m.delay !== undefined).map((m) => m.delay!)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const sizes = msgs.filter((m) => m.length !== undefined).map((m) => m.length!)
      let logPoint: logPoint = {
        time: Math.min(...msgs.map((t) => t.sent)) - _WebsocketLogger.accumulatedData.start,
        count: msgs.length,
        delay: {
          average: computeMean(delays),
          max: computeMax(delays),
          min: computeMin(delays),
          median: computeMedian(delays),
          stdev: computeStdev(delays),
        },
        size: {
          average: computeMean(sizes),
          max: computeMax(sizes),
          min: computeMin(sizes),
          median: computeMedian(sizes),
          stdev: computeStdev(sizes),
        },
      }

      if (!Array.isArray(_WebsocketLogger.accumulatedData.eventMap['msg:sent'])) {
        _WebsocketLogger.accumulatedData.eventMap['msg:sent'] = []
      }
      _WebsocketLogger.accumulatedData.eventMap['msg:sent'].push(logPoint)
      // BUG, FIXME, TODO: this will delete messages, even if we haven't received enough information
      // to calculate the delay yet
      // We should instead only clear messages that are "done" and have been sent already
      _WebsocketLogger._sentMessages.clear()
    }
    _WebsocketLogger.maybeDispatchPayload()
  }

  /**
   * Prepares the beacon to be sent. This takes the existing data structure and changes the
   * representation into a much smaller and flatter data structure, resulting in a much smaller
   * (but harder to read) beacon.
   *
   */
  private static preparePayload() {
    type payloadType = {
      start: number
      sessionId: string
      eventMap: {
        [key in EVENT_TYPES]: number[]
      }
    }
    const data = _WebsocketLogger.accumulatedData
    const flatEntryList = (Object.entries(data.eventMap) as [keyof typeof EVENT_TYPES, logPoint[]][]).map(
      ([evtName, logPoints]) => {
        const flatLp = logPoints.map((lp) =>
          Object.values(lp).map((val) => {
            if (typeof val === 'number') {
              return val
            }
            return Object.values(val) as number[]
          }),
        )
        const flattened = flatLp.flat(2)
        return [evtName, flattened] as [typeof evtName, number[]]
      },
    )

    const payload: payloadType = {
      ...data,
      eventMap: Object.fromEntries(flatEntryList) as payloadType['eventMap'],
    }
    return payload
  }
  /**
   * reset `accumulatedData`
   */
  static clear() {
    _WebsocketLogger.accumulatedData = {
      start: new Date().getTime(),
      sessionId: _WebsocketLogger.sessionId,
      eventMap: {},
    }
  }
  /**
   * Sends the beacon, unless it is empty
   */
  static sendPayload() {
    const payload = _WebsocketLogger.preparePayload()
    const allDataPoints = ([] as number[]).concat(...Object.values(payload.eventMap))
    if (allDataPoints.length > 0) {
      const beaconEnqueued = navigator.sendBeacon(TELEMETRY_BEACON_ENDPOINT, new Blob([JSON.stringify(payload)]))
      if (beaconEnqueued) {
        _WebsocketLogger.clear()
      }
    }
  }

  /**
   * Check if the beacon exceeds the size treshold, and if so, sends it
   *
   * This is done because:
   * - we don't want to send a gigantic beacon, the browser may crash and we lose valuable telemetry
   * - chromium browsers will not send beacons over 64kb in size
   */
  private static maybeDispatchPayload() {
    const preparedPayload = JSON.stringify(_WebsocketLogger.preparePayload())
    if (preparedPayload.length >= PAYLOAD_LENGTH_TRESHOLD_BEFORE_DISPATCH) {
      _WebsocketLogger.sendPayload()
    }
  }
}

setInterval(() => {
  _WebsocketLogger.flush()
}, STATS_RESOLUTION_MS)

/**
 * Try to dispatch the beacon if this is our last chance to do so
 */
window.addEventListener('beforeunload', _WebsocketLogger.sendPayload)

/**
 * There are many reasons why beforeunload can fail to fire,
 * such as the user closing the application (in a mobile device) rather than the page
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    _WebsocketLogger.sendPayload()
  }
})
/*
 * Safari doesn't fire the visibilitychange event when navigating away from a document
 */
if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
  window.addEventListener('pagehide', () => {
    _WebsocketLogger.sendPayload()
  })
}

export const WebsocketLogger = new _WebsocketLogger()
